import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Row,
  Col,
  Card,
  Tabs,
  Tab,
  Form,
  Alert,
  Badge,
  Button,
  Breadcrumb,
  Placeholder,
} from "react-bootstrap";
import {
  FaChartLine,
  FaUsers,
  FaExclamationTriangle,
  FaBullseye,
  FaCalendarAlt,
} from "react-icons/fa";
import Title from "../../common/Title";
import { usageService } from "../../../services/usage.service";
import { canAccessSuperUserAnalytics } from "../../../utils/authUtils";
import {
  MONTH_RANGE_OPTIONS,
  formatUnits,
  formatChange,
  getConfidenceVariant,
  lastCompleteMonthLabel,
  USAGE_STATUS,
} from "../../../utils/usageUtils";
import UsageInsights from "../UsageInsights";
import UsageTrendChart from "../UsageTrendChart";
import UsageAnalytics from "../UsageAnalytics";
import UsageClientsTable from "./UsageClientsTable";
import TierManagement from "./TierManagement";
import "../../analytics/superuser/SuperUserAnalytics.css";

const FORECAST_MONTHS = 3;

/**
 * Clickable tier-pressure counts that drive the clients table filter.
 *
 * Deliberately uncoloured: four large red / amber / green / grey blocks side by side
 * read as an alarm panel rather than a filter, and the status badges in the table
 * already carry the colour coding. Selection is shown by the filled button instead.
 */
function TierPressureStrip({ summary, statusFilter, onSelect }) {
  const bands = [
    { status: USAGE_STATUS.OVER, label: "Over limit", count: summary.clientsOverLimit },
    { status: USAGE_STATUS.WARN, label: "Approaching", count: summary.clientsInWarnBand },
    { status: USAGE_STATUS.OK, label: "Within tier", count: summary.clientsOk },
    { status: USAGE_STATUS.NO_TIER, label: "No tier", count: summary.clientsWithoutTier },
  ];

  return (
    <Card className="mb-4">
      <Card.Header>Tier pressure</Card.Header>
      <Card.Body>
        <Row className="g-2">
          {bands.map((band) => (
            <Col key={band.status} xs={6} md={3}>
              <Button
                variant={statusFilter === band.status ? "secondary" : "outline-secondary"}
                className="w-100 d-flex flex-column py-2"
                onClick={() => onSelect(statusFilter === band.status ? null : band.status)}
              >
                <span className="fs-4 fw-semibold">{band.count}</span>
                <span className="small">{band.label}</span>
              </Button>
            </Col>
          ))}
        </Row>
        <div className="text-muted small mt-2">
          {summary.clientsProjectedOverLimit} client
          {summary.clientsProjectedOverLimit === 1 ? " is" : "s are"} on course to pass their limit by
          month end on the current run rate.
        </div>
      </Card.Body>
    </Card>
  );
}

/**
 * Product Team view of usage across every client: portfolio totals, tier pressure, a
 * per-client table, and tier configuration.
 */
function UsageAnalyticsOverview({ user, hideTitle }) {
  const navigate = useNavigate();
  const params = useParams();
  const [viewingClientId, setViewingClientId] = useState(null);
  // Windowed (hideTitle) mode drills into a client via local state instead of a route
  // change — navigating away from /product would close every open desktop window.
  const clientId = hideTitle ? viewingClientId : params.clientId;

  const [months, setMonths] = useState(12);
  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState([]);
  const [statusFilter, setStatusFilter] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");

  const allowed = canAccessSuperUserAnalytics(user);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const params = { months, forecastMonths: FORECAST_MONTHS };
      const [summaryResult, rowsResult] = await Promise.all([
        usageService.getPortfolioSummary(params),
        usageService.getClientRows({ months }),
      ]);

      // .data because api/http.js has no response interceptor — services hand back the
      // full axios response, matching AllClientsAnalytics.
      setSummary(summaryResult.data);
      setRows(rowsResult.data || []);
    } catch (err) {
      setError(err?.response?.data || err?.message || "Unable to load usage analytics.");
    } finally {
      setLoading(false);
    }
  }, [months]);

  useEffect(() => {
    // Skip the portfolio fetch entirely while drilled into a single client — that view
    // loads its own data.
    if (allowed && !clientId) load();
  }, [allowed, clientId, load]);

  if (!allowed) {
    return (
      <Alert variant="danger">
        You do not have permission to view cross-client usage analytics.
      </Alert>
    );
  }

  // Drill-down: reuse the client page rather than duplicating it.
  if (clientId) {
    return (
      <div>
        {!hideTitle && <Title text="Client Usage" icon={<FaChartLine className="mb-1" />} />}
        <Breadcrumb>
          <Breadcrumb.Item
            onClick={() => (hideTitle ? setViewingClientId(null) : navigate("/product/usage-analytics"))}
            role="button"
          >
            Usage Analytics
          </Breadcrumb.Item>
          <Breadcrumb.Item active>{clientId}</Breadcrumb.Item>
        </Breadcrumb>
        <UsageAnalytics user={user} clientId={clientId} embedded />
      </div>
    );
  }

  // The completed month the projection is measured against.
  const priorMonthLabel = lastCompleteMonthLabel(summary?.series);

  const insightCards = summary
    ? [
        {
          label: `${summary.currentMonthLabel} so far`,
          value: formatUnits(summary.currentMonthUnits),
          icon: <FaCalendarAlt size={16} />,
          caption: "in-progress month",
        },
        {
          label: "Projected month end",
          value: formatUnits(summary.projectedCurrentMonthUnits),
          icon: <FaBullseye size={16} />,
          trend: summary.monthOverMonthPercent,
          caption: priorMonthLabel ? `vs ${priorMonthLabel}` : "no prior month",
        },
        {
          label: "Clients with usage",
          value: `${summary.clientsWithUsageThisMonth} / ${summary.clientCount}`,
          icon: <FaUsers size={16} />,
          caption: "active this month",
        },
        {
          // The only card here where the number itself is good/bad news — keeps the
          // semantic colour the other three deliberately don't carry (see
          // TierPressureStrip above for the same reasoning on the strip below).
          label: "Over limit",
          value: summary.clientsOverLimit,
          icon: <FaExclamationTriangle size={16} />,
          tone: summary.clientsOverLimit > 0 ? "danger" : "success",
          caption: `${summary.clientsProjectedOverLimit} projected to breach`,
        },
      ]
    : [];

  return (
    <div>
      {!hideTitle && <Title text="Usage Analytics" icon={<FaChartLine className="mb-1" />} />}

      {error && <Alert variant="danger">{String(error)}</Alert>}

      <Tabs
        id="usage-analytics-tabs"
        activeKey={activeTab}
        onSelect={(key) => setActiveTab(key)}
        className="mb-3"
        mountOnEnter
      >
        <Tab eventKey="overview" title="Overview">
          {/* Filter card, matching the Azure Costs filter bar. */}
          <Card className="mb-3">
            <Card.Body>
              <Row className="align-items-end g-2">
                <Col md={3}>
                  <Form.Group controlId="usagePeriod">
                    <Form.Label>Period</Form.Label>
                    <Form.Select
                      size="sm"
                      value={months}
                      onChange={(e) => setMonths(Number(e.target.value))}
                    >
                      {MONTH_RANGE_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={9}>
                  {summary?.lastUpdated && (
                    <div className="small text-muted">
                      Usage last refreshed {new Date(summary.lastUpdated).toLocaleString()}
                    </div>
                  )}
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {loading && (
            <Placeholder as="div" animation="glow">
              <Placeholder xs={12} style={{ height: "300px" }} />
            </Placeholder>
          )}

          {!loading && summary && (
            <>
              <UsageInsights cards={insightCards} />

              <TierPressureStrip
                summary={summary}
                statusFilter={statusFilter}
                onSelect={(status) => {
                  setStatusFilter(status);
                  setActiveTab("clients");
                }}
              />

              <Card>
                <Card.Header className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                  <span>Portfolio usage units by month</span>
                  {summary.forecast?.length > 0 && (
                    <span className="small text-muted">
                      Forecast confidence{" "}
                      <Badge bg={getConfidenceVariant(summary.forecasting?.confidence)}>
                        {summary.forecasting?.confidence}
                      </Badge>
                    </span>
                  )}
                </Card.Header>
                <Card.Body>
                  <UsageTrendChart
                    series={summary.series}
                    forecast={summary.forecast}
                    projectedCurrent={summary.projectedCurrentMonthUnits}
                  />
                  <div className="text-muted small mt-2">
                    Growth compares the <strong>projected</strong> current month against the last
                    completed month — the in-progress month's actual total is always lower simply
                    because the month is not over.
                  </div>
                </Card.Body>
                {summary.lastUpdated && (
                  <Card.Footer className="text-muted small">
                    Usage last refreshed {new Date(summary.lastUpdated).toLocaleString()}
                  </Card.Footer>
                )}
              </Card>
            </>
          )}
        </Tab>

        <Tab eventKey="clients" title={`Clients${rows.length ? ` (${rows.length})` : ""}`}>
          {loading ? (
            <Placeholder as="div" animation="glow">
              <Placeholder xs={12} style={{ height: "300px" }} />
            </Placeholder>
          ) : (
            <UsageClientsTable
              rows={rows}
              statusFilter={statusFilter}
              onStatusFilterChange={setStatusFilter}
              onSelectClient={(id) =>
                hideTitle ? setViewingClientId(id) : navigate(`/product/usage-analytics/client/${id}`)
              }
            />
          )}
        </Tab>

        <Tab eventKey="tiers" title="Tiers">
          <TierManagement user={user} onChanged={load} />
        </Tab>
      </Tabs>
    </div>
  );
}

export default UsageAnalyticsOverview;
